import React, { useCallback, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  useFetchOrgDataQuery,
  useFetchOrganizationUsersQuery,
} from "../../services/profileService";
import ReportVersionsModal from "./ReportVersionsModal";
import AddUsersFromOrgModal from "./AddUsersFromOrgModal";

const ROLE_TYPE = {
  managers: "manager",
  employees: "employee",
};

const normalizeUsersList = (payload) => {
  const inner = payload?.data;
  if (Array.isArray(inner)) return inner;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getUserDisplayName = (user) => {
  if (!user || typeof user !== "object") return "—";
  if (user.name) return String(user.name);
  const parts = [user.first_name, user.last_name].filter(Boolean);
  if (parts.length) return parts.join(" ");
  if (user.full_name) return String(user.full_name);
  return "—";
};

const OrganizationMembers = () => {
  const { user } = useSelector((state) => state.auth);
  const { id: organizationIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const organizationId = Number.parseInt(String(organizationIdParam ?? ""), 10);
  const hasValidOrgId = Number.isFinite(organizationId) && organizationId > 0;

  const [activeRoleType, setActiveRoleType] = useState(ROLE_TYPE.managers);
  const [searchQuery, setSearchQuery] = useState("");
  const [reportModal, setReportModal] = useState({
    open: false,
    userId: null,
    userName: "",
  });
  const [addUsersModalOpen, setAddUsersModalOpen] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState({
    open: false,
    message: "",
  });

  const handleOpenReportModal = useCallback((userId, userName) => {
    setReportModal({ open: true, userId, userName });
  }, []);

  const handleCloseReportModal = useCallback(() => {
    setReportModal({ open: false, userId: null, userName: "" });
  }, []);

  const handleOpenAddUsersModal = useCallback(() => {
    setAddUsersModalOpen(true);
  }, []);

  const handleCloseAddUsersModal = useCallback(() => {
    setAddUsersModalOpen(false);
  }, []);

  const handleAddUsersSuccess = useCallback((message) => {
    setSuccessSnackbar({ open: true, message });
  }, []);

  const handleSnackbarClose = useCallback((_event, reason) => {
    if (reason === "clickaway") return;
    setSuccessSnackbar({ open: false, message: "" });
  }, []);

  const organizationNameFromState = location.state?.organizationName;

  const { data: orgPayload } = useFetchOrgDataQuery(organizationIdParam, {
    skip: !hasValidOrgId || Boolean(organizationNameFromState),
  });

  const organizationName = useMemo(() => {
    if (organizationNameFromState) {
      return String(organizationNameFromState);
    }
    const fromApi =
      orgPayload?.data?.organization_name ??
      orgPayload?.data?.name ??
      orgPayload?.organization_name;
    return fromApi ? String(fromApi) : "";
  }, [organizationNameFromState, orgPayload]);

  const {
    data: usersPayload,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useFetchOrganizationUsersQuery(
    { organizationId, roleType: activeRoleType },
    { skip: !hasValidOrgId || user?.role !== "superadmin" }
  );

  const users = useMemo(() => normalizeUsersList(usersPayload), [usersPayload]);

  const rows = useMemo(() => {
    return users.map((member, index) => ({
      id: member.id ?? member.user_id ?? `member-${index}`,
      name: getUserDisplayName(member),
      email: member.email ?? "—",
      role:
        member.role_type ??
        member.role ??
        (activeRoleType === ROLE_TYPE.managers ? "manager" : "employee"),
    }));
  }, [users, activeRoleType]);

  const filteredRows = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const idText = String(row.id ?? "").toLowerCase();
      const nameText = String(row.name ?? "").toLowerCase();
      const emailText = String(row.email ?? "").toLowerCase();
      const roleText = String(row.role ?? "").toLowerCase();
      return (
        idText.includes(q) ||
        nameText.includes(q) ||
        emailText.includes(q) ||
        roleText.includes(q)
      );
    });
  }, [rows, searchQuery]);

  const handleTabChange = useCallback((_event, newRoleType) => {
    setActiveRoleType(newRoleType);
    setSearchQuery("");
  }, []);

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "ID",
        minWidth: 90,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "role",
        headerName: "Role",
        minWidth: 140,
        valueFormatter: (value) => {
          if (!value) return "—";
          return String(value)
            .split("_")
            .map((w) =>
              w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""
            )
            .join(" ");
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 100,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="View report versions">
            <EyeIconBtn
              onClick={() =>
                handleOpenReportModal(params.row.id, params.row.name)
              }
              aria-label={`View reports for ${params.row.name}`}
            >
              <VisibilityIcon fontSize="small" />
            </EyeIconBtn>
          </Tooltip>
        ),
      },
    ],
    [handleOpenReportModal]
  );

  if (user?.role !== "superadmin") {
    return <Navigate to="/view-org" replace />;
  }

  if (!hasValidOrgId) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <Alert severity="error" sx={{ mb: 2 }}>
            Invalid organization. Please return to the organizations list.
          </Alert>
          <CustomBtn onClick={() => navigate("/view-org")}>Back to organizations</CustomBtn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  const errorMessage =
    (error && typeof error === "object" && error.message) ||
    (error && typeof error === "object" && error.data && error.data.message) ||
    "Unable to load members. Please try again.";

  const tabLabel =
    activeRoleType === ROLE_TYPE.managers ? "Managers" : "Employees";
  const titleSuffix = organizationName
    ? ` — ${organizationName}`
    : ` (ID: ${organizationId})`;

  return (
    <SectionWrap component="section">
      <ReportVersionsModal
        open={reportModal.open}
        onClose={handleCloseReportModal}
        userId={reportModal.userId}
        userName={reportModal.userName}
      />

      <AddUsersFromOrgModal
        open={addUsersModalOpen}
        onClose={handleCloseAddUsersModal}
        onSuccess={handleAddUsersSuccess}
        targetOrgId={organizationId}
      />

      <Snackbar
        open={successSnackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
          sx={{ borderRadius: "7px" }}
        >
          {successSnackbar.message}
        </Alert>
      </Snackbar>

      <ContainerWrap>
        <RowWrap sx={{ marginBottom: "10px", alignItems: "center" }}>
          <Col12Wrap
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <TitleWrap sx={{ textTransform: "capitalize", marginBottom: 0 }}>
              {tabLabel}
              {titleSuffix}
            </TitleWrap>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
              <AddUsersBtn
                onClick={handleOpenAddUsersModal}
                startIcon={<PersonAddIcon fontSize="small" />}
              >
                Add Users
              </AddUsersBtn>
              <CustomBtn
                variant="outlined"
                onClick={() => navigate("/view-org")}
                sx={{
                  background: "#ffffff",
                  color: "#2C2C2E",
                  borderColor: "#DADADA",
                  "&:hover": {
                    background: "#f5f5f5",
                    borderColor: "#DADADA",
                  },
                }}
              >
                Back
              </CustomBtn>
            </Box>
          </Col12Wrap>
        </RowWrap>

        <RowWrap sx={{ marginBottom: "16px" }}>
          <Col12Wrap>
            <TabsWrap
              value={activeRoleType}
              onChange={handleTabChange}
              aria-label="Organization members by role"
            >
              <Tab label="Managers" value={ROLE_TYPE.managers} />
              <Tab label="Employees" value={ROLE_TYPE.employees} />
            </TabsWrap>
          </Col12Wrap>
        </RowWrap>

        {isError && (
          <RowWrap sx={{ marginBottom: "16px" }}>
            <Col12Wrap>
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              >
                {String(errorMessage)}
              </Alert>
            </Col12Wrap>
          </RowWrap>
        )}

        <RowWrap sx={{ marginBottom: "20px" }}>
          <Col6Wrap sx={{ maxWidth: { xs: "100%", md: "50%" } }}>
            <FormGroupWrap sx={{ maxWidth: "100%", marginLeft: 0 }}>
              <CustomTextField
                variant="outlined"
                placeholder={`Search ${tabLabel.toLowerCase()} by ID, name, or email`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
              />
            </FormGroupWrap>
          </Col6Wrap>
        </RowWrap>

        <RowWrap>
          <Col12Wrap>
            <TableContentwrap>
              {isLoading || isFetching ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 2,
                    py: 4,
                  }}
                >
                  <CircularProgress size={28} />
                  <Typography>Loading {tabLabel.toLowerCase()}…</Typography>
                </Box>
              ) : (
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 30 },
                    },
                  }}
                  pageSizeOptions={[30, 50, 100]}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  localeText={{
                    noRowsLabel: `No ${tabLabel.toLowerCase()} found for this organization`,
                  }}
                  sx={{
                    "& .MuiDataGrid-iconButtonContainer": {
                      visibility: "visible !important",
                      width: "auto !important",
                      svg: {
                        opacity: "1 !important",
                      },
                    },
                  }}
                />
              )}
            </TableContentwrap>
          </Col12Wrap>
        </RowWrap>
      </ContainerWrap>
    </SectionWrap>
  );
};

export default OrganizationMembers;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0",
}));
const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));
const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const Col6Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "50%",
  paddingLeft: "15px",
  paddingRight: "15px",
  "@media (max-width: 991px)": {
    maxWidth: "100%",
    marginTop: "10px",
    marginBottom: "10px",
  },
}));
const TitleWrap = styled(Typography)(() => ({
  fontSize: "28px",
  fontWeight: "600",
  lineHeight: "1.445",
  color: "#000000",
  marginBottom: "15px",
}));
const TabsWrap = styled(Tabs)(() => ({
  minHeight: "42px",
  borderBottom: "1px solid #E5E7EB",
  "& .MuiTab-root": {
    textTransform: "capitalize",
    fontSize: "16px",
    fontWeight: "500",
    color: "#6B7280",
    minHeight: "42px",
  },
  "& .Mui-selected": {
    color: "#1085AB !important",
  },
  "& .MuiTabs-indicator": {
    backgroundColor: "#1085AB",
  },
}));
const TableContentwrap = styled(Box)(() => ({
  padding: "15px",
  background: "#ffffff",
  borderRadius: "7px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
}));
const FormGroupWrap = styled(Box)(() => ({
  maxWidth: "420px",
  marginLeft: "auto",
  marginBottom: "0px",
  position: "relative",
  "@media (max-width: 991px)": {
    maxWidth: "100%",
  },
}));
const CustomTextField = styled(TextField)(() => ({
  width: "99%",
  border: "1px solid #DADADA",
  background: "#ffffff",
  borderRadius: "7px",
  "& .MuiInputBase-input": {
    fontSize: "16px",
    fontWeight: "300",
    lineHeight: "1",
    color: "#333333",
    padding: "10px 20px",
  },
  "& fieldset": {
    border: "none",
  },
}));
const CustomBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "5px",
  padding: "10px 16px",
  margin: "0",
  boxShadow: "none !important",
}));
const EyeIconBtn = styled(IconButton)(() => ({
  color: "#ffffff",
  background: "#1085AB",
  borderRadius: "5px",
  padding: "7px",
  "&:hover": {
    background: "#0c6d8e",
  },
}));

const AddUsersBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#1085AB",
  borderColor: "#1085AB",
  borderRadius: "5px",
  padding: "10px 16px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#0c6d8e",
    borderColor: "#0c6d8e",
  },
}));
